import type { EditorBody } from "@/components/document-editor"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import AppLayout from "@/layouts/AppLayout"
import { uploadEditorFile } from "@/lib/upload"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { IconArrowLeft, IconTrash } from "@tabler/icons-react"
import { Suspense, lazy, useEffect, useRef, useState } from "react"
import { BoardNavigator } from "../Boards/components/BoardNavigator"
import type { BoardFieldDefinition } from "../Boards/types"
import {
  type Activity,
  type Comment,
  type Navigation,
  PRIORITY_LABELS,
  type Person,
  type Priority,
  type TicketDetail,
} from "../Boards/types"
import { ActivityFeed } from "./components/ActivityFeed"
import { CommentThread } from "./components/CommentThread"
import { CustomField } from "./components/CustomField"
import type { FieldValue } from "./components/CustomField"

const DocumentEditor = lazy(() =>
  import("@/components/document-editor").then((module) => ({ default: module.DocumentEditor })),
)

const AUTOSAVE_DELAY = 800
const UNASSIGNED = "unassigned"

interface Props {
  [key: string]: unknown
  ticket: TicketDetail
  board: { key: string; name: string }
  columns: { id: number; name: string }[]
  assignable_users: Person[]
  comments: Comment[]
  activities: Activity[]
  navigation: Navigation
  fields: BoardFieldDefinition[]
  field_values: Record<string, FieldValue>
}

export default function TicketsShow() {
  const { ticket, board, columns, assignable_users, comments, activities, navigation, fields, field_values } =
    usePage<Props>().props

  const [title, setTitle] = useState(ticket.title)
  const [saving, setSaving] = useState(false)
  const [savedOnce, setSavedOnce] = useState(false)

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pending = useRef<{ title: string; body?: EditorBody }>({ title: ticket.title })
  const loadedKey = useRef(ticket.key)

  useEffect(() => {
    if (loadedKey.current === ticket.key) return

    loadedKey.current = ticket.key
    setTitle(ticket.title)
    setSavedOnce(false)
    pending.current = { title: ticket.title }
  }, [ticket.key, ticket.title])

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  const scheduleSave = () => {
    if (!ticket.can_edit) return
    if (timer.current) clearTimeout(timer.current)

    timer.current = setTimeout(() => {
      router.patch(
        `/tickets/${ticket.key}`,
        { ticket: pending.current },
        {
          preserveState: true,
          preserveScroll: true,
          only: ["ticket"],
          onStart: () => setSaving(true),
          onSuccess: () => setSavedOnce(true),
          onFinish: () => setSaving(false),
        },
      )
    }, AUTOSAVE_DELAY)
  }

  const saveField = (attributes: Record<string, string | number | null>) =>
    router.patch(
      `/tickets/${ticket.key}`,
      { ticket: attributes },
      { preserveScroll: true, only: ["ticket", "activities", "navigation"] },
    )

  const saveFieldValue = (fieldId: number, value: FieldValue) =>
    router.patch(
      `/tickets/${ticket.key}`,
      { ticket: { field_values: { [fieldId]: value } } },
      { preserveScroll: true, only: ["ticket", "field_values", "activities"] },
    )

  return (
    <>
      <Head title={`${ticket.key} ${ticket.title}`} />

      <div className="flex flex-1">
        <BoardNavigator navigation={navigation} />

        <main className="min-w-0 flex-1 px-8 py-10">
          <div className="mx-auto max-w-5xl">
            <Link
              href={`/boards/${board.key}`}
              className="text-muted-foreground mb-4 inline-flex items-center gap-1 text-xs hover:underline"
            >
              <IconArrowLeft className="size-3" />
              {board.name}
            </Link>

            <div className="flex flex-col gap-8 lg:flex-row">
              <div className="min-w-0 flex-1 space-y-6">
                <div>
                  <div className="mb-1 flex items-center gap-2">
                    <span className="text-muted-foreground font-mono text-sm">{ticket.key}</span>
                    {ticket.completed && <Badge variant="outline">Done</Badge>}
                    <span className="text-muted-foreground ml-auto text-xs" aria-live="polite">
                      {saving ? "Saving…" : savedOnce ? "Saved" : ""}
                    </span>
                  </div>

                  <Input
                    value={title}
                    readOnly={!ticket.can_edit}
                    aria-label="Ticket title"
                    className="h-auto border-0 bg-transparent px-0 !text-2xl font-semibold shadow-none focus-visible:ring-0 dark:bg-transparent"
                    onChange={(event) => {
                      setTitle(event.target.value)
                      pending.current = { ...pending.current, title: event.target.value }
                      scheduleSave()
                    }}
                  />
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Description</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="editor-flush">
                      <Suspense fallback={<Skeleton className="h-24 w-full" />}>
                        <DocumentEditor
                          key={ticket.key}
                          initialBody={ticket.body}
                          editable={ticket.can_edit}
                          uploadFile={(file) => uploadEditorFile(`/tickets/${ticket.key}/files`, file)}
                          onChange={(body) => {
                            pending.current = { ...pending.current, body }
                            scheduleSave()
                          }}
                        />
                      </Suspense>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Comments</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CommentThread ticketKey={ticket.key} comments={comments} canComment={ticket.can_edit} />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ActivityFeed activities={activities} />
                  </CardContent>
                </Card>
              </div>

              <aside aria-label="Ticket details" className="w-full shrink-0 lg:w-72">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="ticket_column">Column</Label>
                      <Select
                        value={String(ticket.board_column_id)}
                        disabled={!ticket.can_edit}
                        onValueChange={(value) =>
                          router.patch(
                            `/tickets/${ticket.key}/move`,
                            { board_column_id: Number(value) },
                            { preserveScroll: true },
                          )
                        }
                      >
                        <SelectTrigger id="ticket_column" className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {columns.map((column) => (
                            <SelectItem key={column.id} value={String(column.id)}>
                              {column.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="ticket_assignee">Assignee</Label>
                      <Select
                        value={ticket.assignee ? String(ticket.assignee.id) : UNASSIGNED}
                        disabled={!ticket.can_edit}
                        onValueChange={(value) =>
                          saveField({ assignee_id: value === UNASSIGNED ? null : Number(value) })
                        }
                      >
                        <SelectTrigger id="ticket_assignee" className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={UNASSIGNED}>Nobody yet</SelectItem>
                          {assignable_users.map((user) => (
                            <SelectItem key={user.id} value={String(user.id)}>
                              {user.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="ticket_priority">Priority</Label>
                      <Select
                        value={ticket.priority}
                        disabled={!ticket.can_edit}
                        onValueChange={(value) => saveField({ priority: value as Priority })}
                      >
                        <SelectTrigger id="ticket_priority" className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="ticket_due">Due date</Label>
                      <Input
                        id="ticket_due"
                        type="date"
                        className="w-full"
                        defaultValue={ticket.due_on ?? ""}
                        readOnly={!ticket.can_edit}
                        onChange={(event) => saveField({ due_on: event.target.value || null })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="ticket_estimate">Estimate (hours)</Label>
                      <Input
                        id="ticket_estimate"
                        type="number"
                        min="0"
                        step="0.25"
                        className="w-full"
                        defaultValue={ticket.estimate ?? ""}
                        readOnly={!ticket.can_edit}
                        onChange={(event) => saveField({ estimate: event.target.value || null })}
                      />
                    </div>

                    {fields.length > 0 && (
                      <>
                        <Separator />
                        {fields.map((field) => (
                          <CustomField
                            key={field.id}
                            field={field}
                            value={field_values[String(field.id)] ?? null}
                            people={assignable_users}
                            disabled={!ticket.can_edit}
                            onChange={(value) => saveFieldValue(field.id, value)}
                          />
                        ))}
                      </>
                    )}

                    {ticket.reporter && (
                      <>
                        <Separator />
                        <p className="text-muted-foreground text-xs">Filed by {ticket.reporter.name}</p>
                      </>
                    )}

                    {ticket.can_destroy && (
                      <>
                        <Separator />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive w-full justify-start"
                          onClick={() => {
                            if (window.confirm(`Delete ${ticket.key}?`)) {
                              router.delete(`/tickets/${ticket.key}`)
                            }
                          }}
                        >
                          <IconTrash className="size-4" />
                          Delete ticket
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>
              </aside>
            </div>
          </div>
        </main>
      </div>
    </>
  )
}

TicketsShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
